import { Injectable } from '@angular/core';
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

@Injectable()
export class GetQuoteDetailsService {
  apiRoot: string;
  masterPolicyApiRoot: string;
  reportingApiRoot: string;
  policyServiceApiRoot: string;
  results: object;
  loading: boolean;
  authToken: string;
  userToken: string;
  headers: HttpHeaders;
  headersPDF: HttpHeaders;

  constructor(private http: HttpClient) {
    this.results = [];
    this.loading = false;
    this.apiRoot = environment.policyEnrollmentAPI;
    this.masterPolicyApiRoot = environment.masterPolicyAPI;
    this.policyServiceApiRoot = environment.policyServiceAPI;
    this.reportingApiRoot = environment.reportingAPI;
    this.authToken = 'unassigned';
    this.headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.authToken}`,
    });
    this.headersPDF = this.headers.set('Content-Type', 'application/pdf');
  }

  public checkToken = () => this.authToken !== 'unassigned';

  public setToken(type: string, newToken: string): void {
    if (type === 'user') {
      this.userToken = newToken;
      this.headers = this.headers.set('UserToken', newToken);
      this.headersPDF = this.headersPDF.set('UserToken', newToken);
    } else {
      this.authToken = newToken;
      this.headers = this.headers.set('Authorization', `Bearer ${newToken}`);
      this.headersPDF = this.headersPDF.set('Authorization', `Bearer ${newToken}`);
    }
  }

  public processErrorDetails = (err) => err?.error?.detail ?? [];

  public dispatchEvents(element, eventName, data) {
    let event;

    if (!data) {
      data = 0;
    }

    // This is true only for IE,firefox
    if (document.createEvent) {
      // To create a mouse event , first we need to create an event and then initialize it.
      event = document.createEvent('MouseEvent');
      event.data = data;
      event.initMouseEvent(eventName, true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
    } else {
      event = new MouseEvent(eventName, {
        view: window,
        bubbles: true,
        cancelable: true,
        detail: 0,
      });
      event.data = data;
    }
    element.dispatchEvent(event);
  }

  public broadcastPackagePrice(packageInfo, modalFeeInfo) {
    /** Create the initial price variable for the event */
    let packagePrice = 0;
    /** Create the data object for the event */
    const packagePricingAndFees = [];

    if (packageInfo.productInfo) {
      // Getting pricing from the inital quote response
      if (packageInfo.deductibleInfo && packageInfo.deductibleInfo.optionPremium) {
        packagePrice += packageInfo.deductibleInfo.optionPremium;
      }
      if (packageInfo.endorsementInfo && packageInfo.endorsementInfo.length > 0) {
        for (let i = 0; i < packageInfo.endorsementInfo.length; i++) {
          packagePrice += packageInfo.endorsementInfo[i].optionPremium;
        }
      }
      if (packageInfo.productInfo && packageInfo.productInfo.length > 0) {
        for (let i = 0; i < packageInfo.productInfo.length; i++) {
          packagePrice += packageInfo.productInfo[i].optionPremium;
        }
      }
    } else if (packageInfo.frequencyPremium) {
      if (modalFeeInfo) {
        // Getting pricing from the quote submission response
        packagePrice += packageInfo.frequencyPremiumIncludingFees;
      } else {
        // Getting pricing from the quote submission response
        packagePrice += packageInfo.frequencyPremium;
      }
    }

    packagePricingAndFees['packagePricing'] = [{ totalPrice: packagePrice.toFixed(2) }];

    if (packageInfo.termFeeInfo && packageInfo.termFeeInfo.length > 0) {
      packagePricingAndFees['termFeeInfo'] = packageInfo.termFeeInfo;
    } else {
      packagePricingAndFees['termFeeInfo'] = {};
    }

    if (modalFeeInfo) {
      packagePricingAndFees['modalFeeInfo'] = modalFeeInfo;
    } else {
      packagePricingAndFees['modalFeeInfo'] = {};
    }

    /** Fire event to tell parent application the user has purchased a policy */
    console.log('SENDING PACKAGE PRICE EVENT: ', packagePricingAndFees);
    this.dispatchEvents(window.top.document, 'insurancePackagePrice', packagePricingAndFees);
  }

  public reportWidgetEvents(data): Observable<any> {
    return this.http.post<any>(this.reportingApiRoot + 'WidgetEvents/SubmitWidgetEvent', JSON.stringify(data), { headers: this.headers });
  }

  public getWidgetSettings(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'GetWidgetSettings', JSON.stringify(data), { headers: this.headers });
  }

  public policyOptIn(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'PolicyOptIn', JSON.stringify(data), { headers: this.headers });
  }

  public policyUpload(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'PolicyUpload', JSON.stringify(data), { headers: this.headers });
  }

  public getThirdPartyCarriers(): Observable<any> {
    return this.http.get<any>(this.apiRoot + 'ThirdPartyCarriers', {
      headers: this.headers,
    });
  }

  public getQuoteByID(quoteID, appSource): Observable<any> {
    let params = new HttpParams();
    params = params.append('appSource', appSource);
    return this.http.get<any>(this.apiRoot + 'GetQuote/' + quoteID, {
      params: params,
      headers: this.headers,
    });
  }

  public getQuote(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'GetQuote', JSON.stringify(data), { headers: this.headers });
  }

  public submitQuote(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'QuoteSubmission', JSON.stringify(data), { headers: this.headers });
  }

  public sendDeclineNotice(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'SendDeclineNotices', JSON.stringify(data), { headers: this.headers });
  }

  public processPayment(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'PaymentSubmission', JSON.stringify(data), { headers: this.headers });
  }

  public getPolicy(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'GetPolicyDetails', JSON.stringify(data), { headers: this.headers });
  }

  public getLocations(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'GetPropertyAddressesList', JSON.stringify(data), { headers: this.headers });
  }

  public downloadPDF(policyID): Observable<any> {
    return this.http.get<any>(this.apiRoot + 'GetPolicyConfirmationDocument/' + policyID, {
      headers: this.headersPDF,
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }
  public sendEmail(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'SendTermsAndConditionEmail', JSON.stringify(data), { headers: this.headers });
  }

  public nextBillingDate(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'NextBillingDate', JSON.stringify(data), { headers: this.headers });
  }

  public getSecurityQuestions(): Observable<any> {
    return this.http.get<any>(this.apiRoot + 'Account/GetSecurityQuestions', { observe: 'response', headers: this.headers });
  }

  public createAccount(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'Account', JSON.stringify(data), { observe: 'response', headers: this.headers });
  }

  public linkPolicy(data): Observable<any> {
    return this.http.post<any>(this.apiRoot + 'LinkUserPolicy', JSON.stringify(data), { observe: 'response', headers: this.headers });
  }

  public getMasterPolicyQuote(data): Observable<any> {
    return this.http.post<any>(this.masterPolicyApiRoot + 'AssetProtect/MasterPolicyQuote', JSON.stringify(data), {
      headers: this.headers,
    });
  }

  public submitMasterPolicyQuote(data): Observable<any> {
    return this.http.post<any>(this.masterPolicyApiRoot + 'AssetProtect/SubmitMasterPolicyQuote', JSON.stringify(data), {
      headers: this.headers,
    });
  }

  public getPolicyCoverageDetails(policyID, appSource): Observable<any> {
    let params = new HttpParams();
    params = params.append('appSource', appSource);
    return this.http.get<any>(this.policyServiceApiRoot + 'GetPolicyCoverage/' + policyID, {
      params: params,
      headers: this.headers,
    });
  }

  public getPolicyPreviewCoverageDetails(data): Observable<any> {
    return this.http.post<any>(this.policyServiceApiRoot + 'PreviewCoverageChange', JSON.stringify(data), { headers: this.headers });
  }

  public policyCoverageProcessPayment(data, userToken): Observable<any> {
    const headersWithUserToken = new HttpHeaders({
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.authToken,
      UserToken: userToken,
    });
    return this.http.post<any>(this.policyServiceApiRoot + 'CoverageChangeBindPolicy', JSON.stringify(data), {
      headers: headersWithUserToken,
    });
  }

  public policyCoverageProcessPaymentFromLD(data): Observable<any> {
    return this.http.post<any>(this.policyServiceApiRoot + 'CoverageChangeBindPolicyFromLD', JSON.stringify(data), {
      headers: this.headers,
    });
  }

  public getAllOffsets(element) {
    let offsetTotal = 0;
    let offsetParentElement;

    // Pull the intial offset value
    offsetTotal += element.offsetTop;
    console.log('Increased the offset total: ', offsetTotal);
    offsetParentElement = element.offsetParent;

    // Pull all other offset values between this iframe and the parent iframe
    while (offsetParentElement && offsetParentElement.tagName !== 'IFRAME') {
      offsetTotal += offsetParentElement.offsetTop;
      console.log('Increased the offset total: ', offsetTotal);
      offsetParentElement = offsetParentElement.offsetParent;
    }

    return offsetTotal;
  }

  public repositionModal(element) {
    console.log('FOUND ELEMENT: ', element);
    const supportPageOffset = window.top.pageXOffset !== undefined;
    const isCSS1Compat = (window.top.document.compatMode || '') === 'CSS1Compat';
    let widgetYOffset = 0;
    let widgetHeightOffset = 0;
    let scrollTop = 20;
    let multiFrame = false;
    let selectedFrame;
    let rpInsuranceWidget = window.top.document.getElementById('rp-insurance-widget');

    if (rpInsuranceWidget === null) {
      console.log('Checking if there is another iframe in the application that contains the insurance widget.');
      const iframeInventory = window.top.document.getElementsByTagName('iframe');
      if (iframeInventory.length > 0) {
        for (let i = 0; i < iframeInventory.length; i++) {
          const innerDoc = iframeInventory[i].contentDocument || iframeInventory[i].contentWindow.document;
          if (innerDoc.getElementById('rp-insurance-widget')) {
            console.log('FOUND ONE! Insurance Widget found.');
            selectedFrame = iframeInventory[i];
            rpInsuranceWidget = innerDoc.getElementById('rp-insurance-widget');
            multiFrame = true;
          }
        }
      }
    }

    /** If the widget is included in a page, get the offset of the widget and the scroll position of the parent page */
    if (rpInsuranceWidget) {
      console.log('rp-insurance-widget FOUND!!!!');
      if (multiFrame) {
        widgetYOffset = this.getAllOffsets(rpInsuranceWidget) + selectedFrame.offsetTop;
      } else {
        widgetYOffset = rpInsuranceWidget.offsetTop;
      }
      console.log('rp-insurance-widget FOUND - widgetYOffset: ', widgetYOffset);
      widgetHeightOffset = rpInsuranceWidget.offsetHeight;
      console.log('rp-insurance-widget FOUND - widgetHeightOffset: ', widgetHeightOffset);
      scrollTop = supportPageOffset
        ? window.top.pageYOffset
        : isCSS1Compat
        ? window.top.document.documentElement.scrollTop
        : window.top.document.body.scrollTop;
      console.log('rp-insurance-widget FOUND - scrollTop: ', scrollTop);
    }

    console.log('GETTING THE SCROLL TOP: ' + scrollTop);
    console.log('GETTING THE WIDGET Y OFFSET: ' + widgetYOffset);

    /** Update the positioning of the modal */
    const widgetContainer = document.querySelector('.container') as HTMLElement;
    const modalObject = document.querySelector('.modal-content') as HTMLElement;
    if (modalObject) {
      modalObject.style.position = 'fixed';
    }

    if (widgetContainer && modalObject) {
      console.log('GETTING THE WIDGET HEIGHT: ' + widgetContainer.offsetHeight);
      console.log('GETTING THE MODAL HEIGHT: ' + modalObject.offsetHeight);

      if (scrollTop - widgetYOffset < 0) {
        console.log('MODAL IS GOING BEYOND THE TOP OF THE WIDGET.');
        modalObject.style.top = 0 + 'px';
      } else if (scrollTop - widgetYOffset + modalObject.offsetHeight > widgetContainer.offsetHeight) {
        console.log('MODAL IS GOING BEYOND THE BOTTOM OF THE WIDGET.');
        modalObject.style.top = widgetContainer.offsetHeight - (modalObject.offsetHeight + 20) + 'px';
      } else {
        console.log('MODAL IS WITHIN THE WIDGET.');
        modalObject.style.top = scrollTop - widgetYOffset + 'px';
      }
      console.log('modal object: ', modalObject);
      console.log('document query : ', document.querySelector('.modal-content'));
      modalObject.scrollIntoView();
    }
  }
}
